import React, { useState, useRef, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { intl } from "LocaleWrapper";

import { PageHeader, Card, Table } from "@cofano/cck";
import usePropertyChangeEffect from "@cofano/cck/hooks/usePropertyChangeEffect";
import { Collapse } from "@mui/material";

import DeleteIcon from "@mui/icons-material/Delete";
import ModeCommentOutlinedIcon from "@mui/icons-material/ModeCommentOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

import { request } from "components/utils/Request2";
import { TableId, useBackendTable } from "components/table";
import { useConfirm } from "components/hooks/useConfirm";
import Helmet from "components/helmet/Helmet";
import useRequest from "components/hooks/useRequest";

import { NotificationDetailsDialog } from "./NotificationDetails";

import { Language } from "imports/Utils";

import { FormattedDate } from "utils/DateUtils";

import "./styling/style.scss";

const Notifications = () => {

	const confirm = useConfirm();

	const [ searchParams, setSearchParams ] = useSearchParams();
	const id = searchParams.get("id");

	const [ totalUnread, { trigger } ] = useRequest({ defaultResponse: 0, url: "insystemnotifications/unread/amount" });
	const [ types ] = useRequest({ defaultResponse: [], url: "messagesettings/types" });

	const [ state, setState ] = useState({
		showInbox: true,
		showOutbox: false,
		selectedType: undefined,
	});

	const [ dialog, setDialog ] = useState({
		open: false,
		notification: null,
	});

	const stateRef = useRef(state);

	const { tableProps, refetch, refetchAfterDelete } = useBackendTable({
		id: TableId.Notifications,
		onChange: async(pagination, sort, filter, callback) => {

			const response = stateRef.current.selectedType ? await request({
				url: "insystemnotifications/inbox/search",
				method: "POST",
				body: {
					includeArchived: false,
					includeRead: false,
					rows: pagination.size,
					start: pagination.page * pagination.size,
					types: [ stateRef.current.selectedType ],
				},
			}) : await request({
				url: `insystemnotifications/${stateRef.current.showInbox ? "inbox" : "outbox"}?start=${pagination.page * pagination.size}&rows=${pagination.size}`,
			});

			callback({
				content: response.results,
				totalResults: response.totalResults,
			});
		},
	});

	const deleteNotification = (row) => {
		request({
			url: `insystemnotifications/archive/${row.id}`,
			method: "DELETE",
		}).then(() => {
			trigger();
			refetchAfterDelete();
		});
	};

	const markAllAsRead = () => {
		request({
			url: "insystemnotifications/read",
			method: "PUT",
		}).then(() => {
			trigger();
			refetch();
		});
	};

	const archiveAll = () => {
		request({
			url: "insystemnotifications/archive",
			method: "DELETE",
		}).then(() => {
			trigger();
			refetch(0);
		});
	};

	const UnreadDiv = ({ row, children }) => <div className={!row.read && !stateRef.current.showOutbox ? "unread" : ""}>{children}</div>;

	usePropertyChangeEffect(() => {
		stateRef.current = state;
		refetch(0);
	}, [ state ]);

	useEffect(() => {
		if (id) {
			request({
				url: `insystemnotifications/${id}`,
			}).then((response) => {
				setDialog({
					open: true,
					notification: response,
				});
			});
		}
	}, [ id ]);

	return (
		<div className="notifications">
			<PageHeader>
				<PageHeader.Title>
					{Language("NOTIFICATIONS")}
					<Helmet title={intl.formatMessage({ id: "NOTIFICATIONS" })} />
				</PageHeader.Title>
			</PageHeader>

			<Card disablePadding>
				<div className='wrapper'>
					<Sidebar
						totalUnread={totalUnread}
						onShowInbox={() => {
							setState({
								showInbox: true,
								showOutbox: false,
								selectedType: undefined,
							});
						}}
						onShowType={(type) => {
							setState({
								showInbox: false,
								showOutbox: false,
								selectedType: type,
							});
						}}
						onShowOutbox={() => {
							setState({
								showInbox: false,
								showOutbox: true,
								selectedType: undefined,
							});
						}}
						types={types} />

					<div className='list'>
						<Table
							{...tableProps}
							onRowClick={row => {
								setDialog({
									open: true,
									notification: row,
								});
							}}
							columns={[ {
								id: "sender",
								title: intl.formatMessage({ id: "NOTIFICATION_SENDER" }),
								render: (row) => <UnreadDiv row={row}>{row.sender ? row.sender.username : intl.formatMessage({ id: "NOTIFICATION_SYSTEM_SENDER" })}</UnreadDiv>,
								width: 200,
								disableFlexGrow: true,
							}, {
								id: "subject",
								title: intl.formatMessage({ id: "NOTIFICATION_SUBJECT" }),
								render: (row) => <UnreadDiv row={row}>{row.subject}</UnreadDiv>,
							}, {
								id: "createdOn",
								title: intl.formatMessage({ id: "DATE" }),
								render: (row) => <UnreadDiv row={row}><FormattedDate date={row.createdOn} /></UnreadDiv>,
								width: 200,
								disableFlexGrow: true,
							}, {
								id: "percentageRead",
								disabled: !state.showOutbox,
								title: intl.formatMessage({ id: "NOTIFICATION_PERCENTAGE_READ" }),
								render: (row) => {

									const percentageRead = Math.round((row.percentageRead * 100) * 10) / 10;

									return (
										<span style={{ color: percentageRead < 100 ? "#D9534F" : "#369F40", fontWeight: 500 }}>
											{`${percentageRead}%`}
										</span>
									);
								},
								width: 200,
								disableFlexGrow: true,
							} ]}
							actions={[ {
								title: intl.formatMessage({ id: "NOTIFICATION_MARK_ALL_READ" }),
								pinned: true,
								onClick: () => {
									confirm({
										type: "WARNING",
										title: intl.formatMessage({ id: "NOTIFICATION_CONFIRM_MARK_ALL_READ" }),
									}).then(() => {
										markAllAsRead();
									});
								},
							}, {
								title: intl.formatMessage({ id: "NOTIFICATION_ARCHIVE_ALL" }),
								pinned: true,
								onClick: () => {
									confirm({
										type: "WARNING",
										title: intl.formatMessage({ id: "NOTIFICATION_CONFIRM_ARCHIVE_ALL" }),
									}).then(() => {
										archiveAll();
									});
								},
							} ]}
							rowActions={[ {
								icon: <DeleteIcon />,
								onClick: (row) => {
									confirm({
										type: "DELETE",
									}).then(() => {
										deleteNotification(row);
									});
								},
							} ]} />
					</div>
				</div>
			</Card>

			<NotificationDetailsDialog
				open={dialog.open}
				onClose={() => {
					setDialog({ open: false, notification: null });
					const newParams = new URLSearchParams(searchParams);
					newParams.delete("id");
					setSearchParams(newParams, { replace: true });
				}}
				notification={dialog.notification}
				isOutbox={state.showOutbox}
			/>
		</div>
	);
};

const Sidebar = ({ totalUnread, onShowInbox, onShowType, onShowOutbox, types }) => {

	const [ activeItem, setActiveItem ] = useState("inbox");

	const groups = [ {
		name: `${intl.formatMessage({ id: "OTHER" })} ${intl.formatMessage({ id: "STATUSES" }).toLowerCase()}`,
		type: 0,
	}, {
		name: `${intl.formatMessage({ id: "CARGO" })} ${intl.formatMessage({ id: "STATUSES" }).toLowerCase()}`,
		type: 1,
	}, {
		name: `${intl.formatMessage({ id: "CALL" })} ${intl.formatMessage({ id: "STATUSES" }).toLowerCase()}`,
		type: 2,
	}, {
		name: `${intl.formatMessage({ id: "VOYAGE" })} ${intl.formatMessage({ id: "STATUSES" }).toLowerCase()}`,
		type: 3,
	} ];

	return (
		<div className='sidebar'>
			<ul>
				<li
					className={activeItem == "inbox" ? "active" : ""}
					onClick={() => {
						setActiveItem("inbox");
						onShowInbox();
					}}>
					<div className='icon'><ModeCommentOutlinedIcon fontSize='small' /></div>
					<div className='text'>{intl.formatMessage({ id: "NOTIFICATION_INBOX" })}</div>
					<div className='badge'>{totalUnread > 0 ? totalUnread : ""}</div>
				</li>

				{groups.map((group, index) => (
					<li className="group" key={index}>
						<Group name={group.name}>
							<ul>
								{[ ...types ].filter(item => item.type === group.type).map((item, typeIndex) => (
									<li
										className={activeItem == item.key ? "active" : ""}
										onClick={() => {
											setActiveItem(item.key);
											onShowType(item.key);
										}}
										key={typeIndex}>
										{item.name}
									</li>
								))}
							</ul>
						</Group>
					</li>
				))}

				<li
					className={activeItem == "outbox" ? "active" : ""}
					onClick={() => {
						setActiveItem("outbox");
						onShowOutbox();
					}}>
					<div className='icon'><SendOutlinedIcon fontSize='small' /></div>
					<div className='text'>{intl.formatMessage({ id: "SENT_NOTIFICATIONS" })}</div>
				</li>
			</ul>
		</div>
	);
};

const Group = ({ name, children }) => {

	const [ open, setOpen ] = useState(false);

	return (
		<React.Fragment>
			<div className='text' onClick={() => setOpen(!open)}>
				<span>{name}</span>
				{open ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
			</div>

			<Collapse in={open} timeout="auto" unmountOnExit>
				{children}
			</Collapse>
		</React.Fragment>
	);
};

export default Notifications;
